import { useState, useEffect, useRef, useCallback } from 'react';
import api from '../../../../api';
import {
  getRowKey,
  canBulkDeleteContact,
  canSelectContactForBulkInEditMode,
  readBulkDeleteResponse,
  removeContactsFromList,
} from './bulkCustomerUtils';

const campaignIsCompleted = (status) => status === 'completed';

/**
 * Bulk selection + delete on CampaignForm (always on; disabled when campaign is completed).
 */
export function useCampaignFormBulkSelection({
  customers,
  setCustomers,
  customersPage,
  customersPerPage,
  campaignStatus,
  campaignId,
  setMessage,
  errorMessageRef,
  editCampaignMode = false,
}) {
  const isRowSelectable = editCampaignMode
    ? canSelectContactForBulkInEditMode
    : canBulkDeleteContact;
  const [selectedKeys, setSelectedKeys] = useState([]);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const selectAllCheckboxRef = useRef(null);

  const bulkDeleteEnabled = !campaignIsCompleted(campaignStatus);
  const bulkDeleteDisabled = !bulkDeleteEnabled || selectedKeys.length === 0;

  useEffect(() => {
    if (campaignIsCompleted(campaignStatus)) {
      setSelectedKeys([]);
    }
  }, [campaignStatus]);

  useEffect(() => {
    const el = selectAllCheckboxRef.current;
    if (!el) return;
    const start = (customersPage - 1) * customersPerPage;
    const end = customersPage * customersPerPage;
    const pageSlice = customers.slice(start, end);
    const rowsThatCanBeDeleted = pageSlice.filter(isRowSelectable);
    const selectedOnPage = rowsThatCanBeDeleted.filter((customer) =>
      selectedKeys.includes(getRowKey(customer))
    );
    el.indeterminate =
      selectedOnPage.length > 0 &&
      selectedOnPage.length < rowsThatCanBeDeleted.length;
  }, [customers, customersPage, customersPerPage, selectedKeys, isRowSelectable]);

  const toggleRow = useCallback(
    (customer) => {
      if (!bulkDeleteEnabled || !isRowSelectable(customer)) return;
      const key = getRowKey(customer);
      if (!key) return;
      setSelectedKeys((prev) =>
        prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
      );
    },
    [bulkDeleteEnabled, isRowSelectable]
  );

  const toggleSelectAllOnPage = useCallback(() => {
    if (!bulkDeleteEnabled) return;
    const start = (customersPage - 1) * customersPerPage;
    const end = customersPage * customersPerPage;
    const keysOnThisPage = customers
      .slice(start, end)
      .filter(isRowSelectable)
      .map(getRowKey)
      .filter(Boolean);
    setSelectedKeys((prev) => {
      const allSelected =
        keysOnThisPage.length > 0 &&
        keysOnThisPage.every((k) => prev.includes(k));
      if (allSelected) return prev.filter((k) => !keysOnThisPage.includes(k));
      return [...new Set([...prev, ...keysOnThisPage])];
    });
  }, [bulkDeleteEnabled, customers, customersPage, customersPerPage, isRowSelectable]);

  const scrollToMessage = useCallback(() => {
    setTimeout(() => {
      const el = errorMessageRef && errorMessageRef.current;
      if (el) {
        el.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
          inline: 'nearest',
        });
      }
    }, 150);
  }, [errorMessageRef]);

  const confirmBulkDelete = useCallback(async () => {
    if (campaignIsCompleted(campaignStatus)) {
      setConfirmOpen(false);
      return;
    }

    const selectedContacts = customers.filter(
      (customer) =>
        selectedKeys.includes(getRowKey(customer)) && isRowSelectable(customer)
    );
    if (selectedContacts.length === 0) {
      setConfirmOpen(false);
      setMessage({ type: 'error', text: 'No valid contacts selected.' });
      setTimeout(() => setMessage(null), 5000);
      return;
    }

    setConfirmOpen(false);

    const savedContactIds = [
      ...new Set(
        selectedContacts.filter((c) => c.id).map((c) => Number(c.id))
      ),
    ].filter((id) => !Number.isNaN(id) && id > 0);
    const localRowKeys = selectedContacts
      .filter((c) => !c.id)
      .map(getRowKey);

    const notify = (type, text) => {
      setMessage({ type, text });
      scrollToMessage();
      setTimeout(() => setMessage(null), 7000);
    };

    try {
      if (campaignId && savedContactIds.length > 0) {
        const response = await api.outbound.bulkDeleteCampaignCustomers(
          campaignId,
          savedContactIds
        );
        const { deletedCount, removed } = readBulkDeleteResponse(
          response,
          savedContactIds.length
        );

        if (removed) {
          setCustomers((prev) =>
            removeContactsFromList(prev, {
              savedIds: savedContactIds,
              localKeys: localRowKeys,
            })
          );
          setSelectedKeys([]);
        }

        if (response?.status) {
          notify(
            'success',
            response.message ||
              `${deletedCount} contact(s) removed from this campaign.`
          );
        } else {
          notify(
            deletedCount > 0 ? 'info' : 'error',
            response?.message ||
              (deletedCount > 0
                ? `${deletedCount} removed; some contacts could not be deleted.`
                : 'Could not delete the selected contacts.')
          );
        }
        return;
      }

      if (localRowKeys.length > 0) {
        setCustomers((prev) =>
          removeContactsFromList(prev, {
            savedIds: [],
            localKeys: localRowKeys,
          })
        );
        setSelectedKeys([]);
        notify(
          'success',
          `${localRowKeys.length} contact(s) removed from the list.`
        );
      }
    } catch (err) {
      console.error('Bulk delete error:', err);
      notify(
        'error',
        err.response?.data?.message ||
          err.message ||
          'An error occurred while deleting contacts.'
      );
    }
  }, [
    campaignStatus,
    campaignId,
    customers,
    selectedKeys,
    setCustomers,
    setMessage,
    scrollToMessage,
    isRowSelectable,
  ]);

  return {
    bulkSelection: {
      getRowKey,
      interactionsEnabled: bulkDeleteEnabled,
      selectedKeys,
      selectAllCheckboxRef,
      isRowSelectable,
      deleteDisabled: bulkDeleteDisabled,
      isLocked: campaignIsCompleted(campaignStatus),
      onToggleRow: toggleRow,
      onToggleSelectAll: toggleSelectAllOnPage,
      onBulkDeleteClick: () => setConfirmOpen(true),
    },
    showBulkConfirmModal: confirmOpen,
    setShowBulkConfirmModal: setConfirmOpen,
    confirmBulkDelete,
  };
}
